import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { adminUploadImage } from "../../lib/api";
import { Plus, Pencil, Trash2, Check, X, GripVertical, Upload, ImageIcon, ChevronUp, ChevronDown } from "lucide-react";

const ListEditor = ({ items, fields, onSave, title }) => {
  const [editIdx, setEditIdx] = useState(-1);
  const [form, setForm] = useState({});

  const emptyForm = () => fields.reduce((a, f) => ({ ...a, [f.key]: f.type === "array" ? [] : "" }), {});

  const startAdd = () => { setEditIdx(items.length); setForm(emptyForm()); };
  const startEdit = (i) => { setEditIdx(i); setForm({ ...items[i] }); };
  const cancel = () => { setEditIdx(-1); setForm({}); };

  const save = () => {
    const required = fields.filter((f) => f.required !== false);
    if (required.some((f) => !form[f.key] || (typeof form[f.key] === "string" && !form[f.key].trim()))) return;
    const newItems = [...items];
    if (editIdx >= items.length) newItems.push(form);
    else newItems[editIdx] = form;
    onSave(newItems);
    cancel();
  };

  const remove = (i) => {
    const newItems = items.filter((_, j) => j !== i);
    onSave(newItems);
  };

  const moveItem = (i, direction) => {
    const newIndex = i + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const newItems = [...items];
    [newItems[i], newItems[newIndex]] = [newItems[newIndex], newItems[i]];
    onSave(newItems);
  };

  const isEditing = editIdx >= 0;

  return (
    <div className="space-y-4" data-testid={`list-editor-${title?.toLowerCase().replace(/\s/g, "-")}`}>
      {isEditing && (
        <div className="bg-white rounded-lg border border-[#B8945A]/40 p-5">
          <h4 className="font-serif text-sm font-bold text-[#1C2B3A] mb-3">{editIdx >= items.length ? "Add" : "Edit"} Item</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.key} className={`space-y-1 ${f.wide ? "md:col-span-2" : ""}`}>
                <Label className="text-xs text-[#7A7A7A]">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder || ""} className="border-[#E5DDD3] text-sm min-h-[60px]" />
                ) : f.type === "array" ? (
                  <Textarea value={(form[f.key] || []).join("\n")} onChange={(e) => setForm({ ...form, [f.key]: e.target.value.split("\n").filter(Boolean) })} placeholder="One per line" className="border-[#E5DDD3] text-sm min-h-[60px]" />
                ) : f.type === "image" ? (
                  <ImageFieldInput value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} placeholder={f.placeholder || "Paste image URL or upload"} testid={`image-input-${f.key}`} />
                ) : (
                  <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder || ""} className="border-[#E5DDD3] text-sm" />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={save} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1"><Check size={14} /> Save</Button>
            <Button variant="outline" onClick={cancel} className="border-[#E5DDD3] text-sm gap-1"><X size={14} /> Cancel</Button>
          </div>
        </div>
      )}
      {!isEditing && (
        <Button onClick={startAdd} className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white text-sm gap-1"><Plus size={14} /> Add {title}</Button>
      )}
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="bg-white rounded-lg border border-[#E5DDD3] p-4 flex items-center gap-3 group" data-testid={`list-item-${title?.toLowerCase().replace(/\s/g, "-")}-${i}`}>
            <div className="flex flex-col flex-shrink-0">
              <button onClick={() => moveItem(i, -1)} disabled={i === 0} className="text-[#D5CFC7] hover:text-[#B8945A] disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`move-up-${i}`} aria-label="Move up"><ChevronUp size={14} /></button>
              <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} className="text-[#D5CFC7] hover:text-[#B8945A] disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`move-down-${i}`} aria-label="Move down"><ChevronDown size={14} /></button>
            </div>
            <GripVertical size={14} className="text-[#D5CFC7] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-sans text-sm text-[#1C2B3A] font-medium truncate">{item[fields[0].key]}</p>
              {fields[1] && <p className="font-sans text-xs text-[#7A7A7A] truncate">{item[fields[1].key]}</p>}
            </div>
            <div className="flex gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="icon" onClick={() => startEdit(i)} className="h-8 w-8 text-[#7A7A7A] hover:text-[#1C2B3A]"><Pencil size={14} /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(i)} className="h-8 w-8 text-[#7A7A7A] hover:text-red-600"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ListEditor;

const ImageFieldInput = ({ value, onChange, placeholder, testid }) => {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      const url = res.url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${res.url}` : res.url;
      onChange(url);
    } catch {}
    setUploading(false);
  };
  const previewSrc = value?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;
  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="border-[#E5DDD3] text-sm flex-1" data-testid={testid} />
        <label className="cursor-pointer flex-shrink-0">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])} />
          <div className="h-9 px-3 flex items-center gap-1 rounded-md border border-[#E5DDD3] hover:border-[#B8945A] text-xs font-sans text-[#7A7A7A]"><Upload size={12} /> {uploading ? "..." : "Upload"}</div>
        </label>
      </div>
      <div className="w-12 h-12 rounded border border-[#E5DDD3] flex items-center justify-center bg-[#FAF8F4] overflow-hidden">
        {value ? <img src={previewSrc} alt="logo" className="w-full h-full object-contain" /> : <ImageIcon size={16} className="text-[#D5CFC7]" />}
      </div>
    </div>
  );
};
